import { useMutation, useQueryClient } from "@tanstack/react-query";

import { API_URL, ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { displayErrorToast, displayToast } from "@/utils/toast";

export interface ArchiveWorkflowVersionRequest {
  workflow_version_id: number;
  restore?: boolean;
}

export interface ArchiveWorkflowVersionResponse {
  success: boolean;
  message: string;
}

export const useArchiveWorkflowVersion = () => {
  const queryClient = useQueryClient();
  const authContext = useAuth();

  return useMutation<ArchiveWorkflowVersionResponse, Error, ArchiveWorkflowVersionRequest>({
    mutationFn: async ({ workflow_version_id, restore }) => {
      const response = await fetch(`${API_URL}/archive-workflow-version`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${authContext?.userToken}`,
        },
        credentials: "include",
        body: JSON.stringify({
          workflow_version_id,
          restore,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(
          errorData.message || `Failed to ${restore ? "restore" : "hide"} workflow version`,
        );
      }

      return response.json();
    },
    onSuccess: async (_, variables) => {
      const message = variables.restore
        ? "Workflow version restored successfully"
        : "Workflow version hidden successfully";
      displayToast(message, ToastType.success);

      // Invalidate all workflow version queries using multiple strategies for reliability

      // Strategy 1: Invalidate by exact endpoint URLs (most reliable)
      await queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.workflow_versions],
        refetchType: "active",
      });

      await queryClient.invalidateQueries({
        queryKey: [ENDPOINTS.workflow_editor_state],
        refetchType: "active",
      });

      // Strategy 2: Invalidate all queries with workflow-version in the key (catches all variations)
      await queryClient.invalidateQueries({
        predicate: query => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes("workflow-version");
        },
        refetchType: "active",
      });

      // Strategy 3: Force refetch all active queries to ensure UI updates
      await queryClient.refetchQueries({
        predicate: query => {
          const keyString = JSON.stringify(query.queryKey);
          return keyString.includes("workflow-version");
        },
        type: "active",
      });
    },
    onError: (error, variables) => {
      const action = variables.restore ? "restore" : "hide";
      displayErrorToast(error.message || `Failed to ${action} workflow version`);
    },
  });
};
